import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-dashboard-milestones',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './dashboard-milestones.component.html',
  styleUrl: './dashboard-milestones.component.scss'
})
export class DashboardMilestonesComponent {
  @Input() overallProgress: number = 72;
  @Input() collegesSaved: number = 0;

  // Account creation date (from ApplicationUser.CreatedOn via the progress-stats endpoint)
  @Input() memberSince: string | null = null;
  
  // TODO: no existing API for these — needs backend team
  @Input() profileCompletedCount: number = 8;
  @Input() documentsUploaded: number = 2;
  @Input() savedScholarships: number = 3;
  
  // Consistency streak (from the login-streak endpoint)
  @Input() consistencyStreak: number = 0;
  @Input() streakActiveToday: boolean = false;

  // Tasks mapping to existing Checklist pending tasks
  @Input() upcomingTasks: any[] = [];

  /**
   * Top 5 upcoming tasks ordered by end date (latest first). Tasks without an
   * end date sink to the bottom so dated deadlines always surface first.
   */
  get sortedUpcomingTasks(): any[] {
    return [...(this.upcomingTasks || [])]
      .sort((a, b) => this.getTime(b.endDate) - this.getTime(a.endDate))
      .slice(0, 5);
  }

  private getTime(date: any): number {
    const t = date ? new Date(date).getTime() : NaN;
    return isNaN(t) ? -Infinity : t;
  }

  /** Whole days from today until the task's end date (0 when overdue/undated). */
  daysLeft(endDate: any): number {
    if (!endDate) return 0;
    const end = new Date(endDate).getTime();
    if (isNaN(end)) return 0;
    const diff = Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  }

  get streakMessage(): string {
    if (this.consistencyStreak <= 0) {
      return 'Log in daily to start!';
    }
    return this.streakActiveToday ? 'Keep it up!' : 'Log in today to keep it going!';
  }
  
  // Timeline/Audit is mocked since no existing timeline API exists
  @Input() journeyEvents: any[] = [
    { title: 'Complete Profile', description: 'You took the first step towards your dream.', icon: 'pi-user', color: '#1E40AF', completed: true, linkLabel: 'Complete Profile', route: '/portal/application-form' },
    { title: 'Documents Uploaded', description: 'Great start! You\'ve uploaded important documents.', icon: 'pi-file', color: '#6D28D9', completed: true, linkLabel: 'Upload Documents', route: '/portal/student-document-vault' },
    { title: 'First Mentor Session Request', description: 'You connection request with a mentor and got expert guidance.', icon: 'pi-calendar', color: '#F59E0B', completed: true, linkLabel: 'View Mentors', route: '/portal/mentor-me' },
    { title: 'University Shortlists', description: 'You shortlisted universities that match your goals.', icon: 'pi-home', color: '#3B82F6', completed: true, linkLabel: 'Discover University', route: '/portal/institute-search' },
    // 'Applications Started' is intentionally always shown as incomplete and pinned to the end.
    { title: 'Applications Started', description: 'You\'ve started your applications. Keep the momentum!', icon: 'pi-send', color: '#10B981', completed: false, alwaysLast: true, linkLabel: 'Track Application', route: '/portal/my-institutes' }
  ];

  /**
   * Journey order is not fixed for the first steps — whoever is completed first
   * floats to the top. Pinned events (e.g. 'Applications Started') always stay last.
   * Sort is stable, so items with the same completion state keep their original order.
   */
  get sortedJourneyEvents(): any[] {
    return this.journeyEvents
      .map((event, index) => ({ event, index }))
      .sort((a, b) => {
        const aPinned = a.event.alwaysLast ? 1 : 0;
        const bPinned = b.event.alwaysLast ? 1 : 0;
        if (aPinned !== bPinned) return aPinned - bPinned;

        const aDone = a.event.completed ? 0 : 1;
        const bDone = b.event.completed ? 0 : 1;
        if (aDone !== bDone) return aDone - bDone;

        return a.index - b.index;
      })
      .map(entry => entry.event);
  }
}
